import Vue from 'vue'
import Vuex from 'vuex'
import jwtDecode from 'jwt-decode'
import { Credentials, EditorialProject, ElementStatusType, Permission, User } from '@/models'
import { APP_CCP, ROLE_ADMIN, ROLE_SPECIAL } from '@/utils/constants'
import { ActionTypes } from './action-types'
import { MutationTypes } from './mutation-types'
import ApiClient from '@/services/api-client'
import router from '@/router'
import { Utils } from '@/utils'

Vue.use(Vuex)

const AUTH_TOKEN = 'authToken'
const USER_INFO = 'userInfo'
const ORIGIN_URL = 'originUrl'

export const state = {
  vm: null,
  user: JSON.parse(localStorage.getItem(USER_INFO) || '{}'),
  token: JSON.parse(localStorage.getItem(AUTH_TOKEN) || '{}'),
  originUrl: localStorage.getItem(ORIGIN_URL) || '',
  activeLang: '',
  isLoading: false,
  editorialProjects: [],
  elementTypes: [],
  elementStatusTypes: [],
  elementStatusPhases: [],
  loadingStatusTypes: false,
  loadingStatusPhase: false
}
export type State = typeof state

export default new Vuex.Store({
  state: state,
  getters: {
    userInfo: (state) => {
      return state.user
    },
    isUserAuthenticated: (state) => {
      if (state.token.accessToken) {
        const jwtToken: { sub: string; iss: string; exp: number } = jwtDecode(
          state.token.accessToken
        )
        if (jwtToken.exp * 1000 >= Date.now()) {
          return true
        }
      }
      return false
    },
    isAdmin: (state) => {
      return (
        state.user &&
        state.user.groups &&
        state.user.groups.includes('admin') === true
      )
    },
    isEditor: (state) => {
      return (
        state.user &&
        state.user.groups &&
        state.user.groups.includes('editor') === true
      )
    },
    isExternal: (state) => {
      return (
        state.user &&
        state.user.username &&
        state.user.username.includes('@colex.grupo-sm.com') === true
      )
    },
    editorialProjects: (state) => {
      return state.editorialProjects
    },
    /**
     * Production projects the user can create products in: like the v-security directive, a CCP
     * ADMIN permission allows every project, otherwise those with an ADMIN or SPECIAL permission.
     * Users without an active account keep the legacy unrestricted behaviour.
     */
    creatableEditorialProjects: (state) => {
      const projects: EditorialProject[] = state.editorialProjects
      const account = state.user.account
      if (!account || !account.accountId || account.active === false) return projects
      if (Utils.hasCcpRole(account, ROLE_ADMIN)) return projects
      const allowed = account.permissions
        .filter((item: Permission) => item.appId === APP_CCP && item.role === ROLE_SPECIAL)
        .map((item: Permission) => item.editorialProjectId)
      return projects.filter(project => allowed.includes(project.id))
    },
    elementTypes: (state) => {
      return state.elementTypes
    },
    isLoading: (state) => {
      return state.isLoading
    }
  },
  mutations: {
    [MutationTypes.SET_VM]: (state: State, vm: any) => {
      state.vm = vm
    },
    [MutationTypes.SET_USER]: (state: State, payload: User) => {
      state.user = payload
    },
    [MutationTypes.SET_TOKEN]: (state: State, token: string) => {
      localStorage.setItem(AUTH_TOKEN, JSON.stringify(token))
      state.token = token
    },
    [MutationTypes.SET_ORIGIN_URL]: (state: State, originUrl: string) => {
      localStorage.setItem(ORIGIN_URL, originUrl)
      state.originUrl = originUrl
    },
    [MutationTypes.SET_ACTIVE_LANG]: (state: State, lang: string) => {
      state.activeLang = lang
    },
    [MutationTypes.SET_LOADING]: (state: State, set: boolean) => {
      state.isLoading = set
    },
    [MutationTypes.SET_EDITORIAL_PROJECTS]: (state: State, payload: any) => {
      state.editorialProjects = payload
    },
    [MutationTypes.SET_ELEMENT_TYPES]: (state: State, payload: any) => {
      state.elementTypes = payload
    },
    [MutationTypes.SET_ELEMENT_STATUS_TYPES]: (state: State, payload: any) => {
      state.elementStatusTypes = payload
    },
    [MutationTypes.SET_ELEMENT_STATUS_PHASES]: (state: State, payload: any) => {
      state.elementStatusPhases = payload
    },
    [MutationTypes.SET_LOADING_STATUS_TYPES]: (state: State, value: boolean) => {
      state.loadingStatusTypes = value
    },
    [MutationTypes.SET_LOADING_STATUS_PHASES]: (state: State, value: boolean) => {
      state.loadingStatusPhase = value
    }
  },
  actions: {
    [ActionTypes.AUTH_LOGIN]({ commit, dispatch }, credentials: Credentials) {
      ApiClient.login(credentials)
        .then((response) => {
          commit(MutationTypes.SET_TOKEN, response)
          commit(MutationTypes.SET_LOADING, false)
          return credentials.username
        })
        .then(async() => {
          await dispatch(ActionTypes.USER_REQUEST)
          dispatch(ActionTypes.REDIRECT_LOGIN)
        })
        .catch(() => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          dispatch(ActionTypes.SHOW_TOAST, { message: state.vm.$t('login_view.form.feedback_error'), title: 'Login', variant: 'danger' })
          commit(MutationTypes.SET_LOADING, false)
        })
    },
    async [ActionTypes.USER_REQUEST]({ commit, state }) {
      return ApiClient.getUserInfo()
        .then((user: User) => {
          const userInfo = {
            ...user,
            displayUsername: user.username.replace('IdentityProvider_', '')
          }
          localStorage.setItem(USER_INFO, JSON.stringify(userInfo))
          commit(MutationTypes.SET_USER, userInfo)

          // Set user properties
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          state.vm.$gtag.set({
            user_properties: { ...state.user, crm_id: state.user.username }
          })
        })
        .catch(() => {
          const user: User = {
            displayUsername: 'displayUsername',
            username: 'Username',
            name: 'Nombre',
            nickname: 'Nickname',
            email: 'mail@grupo-sm.com',
            groups: ['user']
          }
          localStorage.setItem(USER_INFO, JSON.stringify(user))
        })
    },
    [ActionTypes.AUTH_LOGOUT]({ commit }) {
      ApiClient.logout()
      localStorage.removeItem(AUTH_TOKEN)
      localStorage.removeItem(USER_INFO)
      commit(MutationTypes.SET_TOKEN, '')
      commit(MutationTypes.SET_USER, {})
      router.push('/login')
    },
    [ActionTypes.REDIRECT_LOGIN]({ commit, state }) {
      let path = state.originUrl
      if (path === '' || path === null || path === '/login') {
        path = '/'
      }
      router.push({ path: path })
        .catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })

      // Clear origin url
      commit(MutationTypes.SET_ORIGIN_URL, '')
    },
    async [ActionTypes.LOAD_EDITORIAL_PROJECTS]({ commit, state }, force = false) {
      if (state.editorialProjects.length < 1 || force) {
        const response = await ApiClient.getEditorialProjects()
        commit(MutationTypes.SET_EDITORIAL_PROJECTS, response)
      }
    },
    async [ActionTypes.LOAD_ELEMENT_TYPES]({ commit, state }) {
      if (state.elementTypes.length < 1) {
        const response = await ApiClient.getElementTypes()
        commit(MutationTypes.SET_ELEMENT_TYPES, response)
      }
    },
    async [ActionTypes.GET_ELEMENT_STATUS_TYPES]({ commit, state }, { editorialProjectId, country }) {
      if (state.elementStatusTypes.length < 1 && !state.loadingStatusTypes) {
        commit(MutationTypes.SET_LOADING_STATUS_TYPES, true)
        const response = await ApiClient.getElementStatusType()
        commit(MutationTypes.SET_ELEMENT_STATUS_TYPES, response)
        commit(MutationTypes.SET_LOADING_STATUS_TYPES, false)
      }
      return state.elementStatusTypes.filter((elementStatusType: ElementStatusType) =>
        elementStatusType.editorialProjectId === editorialProjectId &&
        elementStatusType.country === country)
    },
    async [ActionTypes.GET_ELEMENT_STATUS_PHASES]({ commit, state }, { editorialProjectId, country }) {
      if (state.elementStatusPhases.length < 1 && !state.loadingStatusPhase) {
        commit(MutationTypes.SET_LOADING_STATUS_PHASES, true)
        const response = await ApiClient.getElementStatusPhase()
        commit(MutationTypes.SET_ELEMENT_STATUS_PHASES, response)
        commit(MutationTypes.SET_LOADING_STATUS_PHASES, false)
      }
      return state.elementStatusPhases.filter((elementStatusPhase: ElementStatusType) =>
        elementStatusPhase.editorialProjectId === editorialProjectId &&
        elementStatusPhase.country === country)
    },
    [ActionTypes.SHOW_TOAST]({ state }, { message, title = 'Información', variant = 'secondary' }) {
      if (!state.vm) {
        return
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      state.vm.$bvToast.toast(message, {
        autoHideDelay: 8000,
        appendToast: true,
        title: title,
        variant: variant
      })
    }
  },
  modules: {}
})
